import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CircularGallery from '../components/CircularGallery';
import { useTranslation } from 'react-i18next';
import { useServices } from '../contexts/services/ServiceContext';
import { useAuth } from '../contexts/auth/AuthContext';
import SEO from '../components/SEO';
import { Reveal } from '../components/motion/Reveal';
import { Stagger } from '../components/motion/Stagger';
import { Card } from '../components/ui/Card';

function Home() {
  const { t } = useTranslation(['home', 'common']);
  const navigate = useNavigate();
  const { services, categories } = useServices();
  const { isAuthenticated } = useAuth();

  const handleTopUpClick = () => {
    navigate(isAuthenticated ? '/profile' : '/login');
  };

  const [showPromoPopup, setShowPromoPopup] = useState(true);

  // Quick booking form state
  const [quickBooking, setQuickBooking] = useState({
    serviceId: '',
    date: ''
  });

  // Get cheapest service from each category for pricing guide
  const getCheapestServiceByCategory = () => {
    const cheapestServices: { [categoryId: string]: any } = {};

    categories.forEach(category => {
      if (category.services && category.services.length > 0) {
        // Find the service with the lowest price in this category
        const cheapest = category.services.reduce((min, service) => {
          const currentPrice = service.numericPrice || parseFloat(String(service.price)) || 0;
          const minPrice = min.numericPrice || parseFloat(String(min.price)) || 0;
          return currentPrice < minPrice ? service : min;
        });

        cheapestServices[category.id] = {
          ...cheapest,
          categoryName: category.name || category.title || 'Service'
        };
      }
    });

    return cheapestServices;
  };

  const cheapestServices = getCheapestServiceByCategory();

  const handlePromoClick = () => {
    navigate('/promotion');
  };

  // Handle quick booking form submission
  const handleQuickBooking = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that a service is selected
    if (!quickBooking.serviceId) {
      alert('Please select a service');
      return;
    }

    // Find the selected service from the services list
    const selectedService = services.find(service => service.id === quickBooking.serviceId);

    if (selectedService) {
      // Store selected service in localStorage (same pattern as Services page)
      localStorage.setItem('selectedService', JSON.stringify({
        id: selectedService.id,
        name: selectedService.name,
        duration: selectedService.numericDuration || 0,
        price: selectedService.numericPrice || 0,
        category_id: selectedService.category_id
      }));

      // Store booking preferences if date is provided
      if (quickBooking.date) {
        localStorage.setItem('bookingPreferences', JSON.stringify({
          preferredDate: quickBooking.date
        }));
      }

      // Navigate to booking page
      navigate('/booking');
    }
  };

  // Toggle this flag to switch between video and image
  const useVideo = true; // set to true for video, false for image
  return (
    <>
      <SEO
        title="Healing Lotus Spa | Luxury Massage, Facials & Wellness in Dallas, TX"
        description="Dallas's premier medical-grade spa offering signature massages, anti-aging facials, body treatments, and wellness memberships. Book your appointment at Healing Lotus Skin Spa."
        path="/"
      />
      <section className="relative w-full h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Original Pexels image: https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1 */}
        <picture>
          <source type="image/webp" srcSet="/images/hero-image.webp" />
          <img
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/hero-image.jpg"
            alt="Healing Lotus Skin Spa in Dallas — tranquil spa ambiance with calming elements"
            width={985}
            height={537}
            fetchPriority="high"
            loading="eager"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/70 via-brand-navy/30 to-brand-navy/55" />
        <Stagger className="relative z-10 flex flex-col items-start justify-center h-full w-full max-w-5xl mx-auto px-6">
          <span className="text-sm md:text-base text-white/80 font-light tracking-[0.18em] uppercase mb-4">Healing Lotus Skin Spa · Dallas, TX</span>
          <h1 className="text-display-xl font-serif font-medium text-white mb-6 drop-shadow-[0_2px_30px_rgba(0,0,0,0.35)]">Where beauty rises from calm</h1>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed font-light">
            We exist for those who seek transformation without spectacle, confidence without noise, and beauty that is grounded&mdash;not manufactured.
          </p>
          <Link to="/services" className="bg-brand-sage hover:bg-brand-sage-deep text-brand-navy hover:text-white px-8 py-3.5 rounded-md font-semibold tracking-wide transition duration-300 inline-flex items-center hover:-translate-y-0.5 hover:shadow-sage active:scale-[0.98]">
            {t('common:buttons.bookYourExperience')}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </Stagger>
      </section>

      {/* La Mer Buy 1 Get 1 Free — Promotion Banner */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-r from-[#F9F3E3] via-[#FDF8EF] to-[#F9F3E3] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-gold/5 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-brand-gold/5 blur-3xl"></div>

        <div className="container mx-auto max-w-5xl relative">
          <div
            onClick={handlePromoClick}
            className="bg-white/85 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border border-brand-gold/20"
          >
            <div className="flex flex-col md:flex-row">
              {/* Visual side — LA MER The Revitalizing Hydration Facial */}
              <div className="w-full md:w-2/5 relative h-64 md:h-auto bg-cream-50">
                <picture>
                  <source type="image/webp" srcSet="/images/lamer.webp" />
                  <img
                    src="/images/lamer.jpeg"
                    alt="LA MER The Revitalizing Hydration Facial at Healing Lotus Skin Spa in Dallas"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-br from-cream-50/20 via-transparent to-cream-50/30"></div>

                {/* Limited-time pill */}
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-4 py-2 bg-[#C41E3A] text-white text-xs font-bold tracking-wider rounded-full shadow-lg animate-pulse">
                    LIMITED TIME · BUY 1 GET 1 FREE
                  </span>
                </div>

                {/* Highlight chips */}
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                  {['Miracle Broth™', '80 min', 'Eye care included'].map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-white/95 backdrop-blur-sm text-brand-navy text-xs font-semibold tracking-wide shadow"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="w-full md:w-3/5 p-6 md:p-10 flex flex-col justify-center">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-brand-gold/10 text-brand-gold text-xs font-semibold tracking-widest uppercase rounded-full border border-brand-gold/30">
                    Open Spa Activity
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-navy mb-2 leading-tight">
                  Buy 1 get 1 <span className="italic text-brand-gold">free.</span>
                </h2>
                <h3 className="text-base md:text-lg font-serif text-brand-navy/70 mb-4 tracking-wide">
                  LA MER · The Revitalizing Hydration Facial
                </h3>
                <ul className="space-y-1.5 mb-5">
                  {[
                    'Powered by La Mer’s legendary Miracle Broth™',
                    'Intense hydration for stressed, dehydrated skin',
                    'Specialized eye care included with every session',
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2 text-brand-navy/75 text-sm md:text-base">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0"></span>
                      {line}
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="font-serif text-3xl md:text-4xl font-semibold text-brand-navy tabular-nums">
                    $239.90
                  </span>
                  <span className="text-brand-navy/60 text-sm">for</span>
                  <span className="font-serif text-2xl md:text-3xl text-[#C41E3A] tabular-nums">
                    2 sessions
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePromoClick();
                  }}
                  className="self-start inline-flex items-center bg-brand-gold hover:brightness-105 text-white px-8 py-3.5 rounded-md font-semibold tracking-wide transition duration-300 hover:shadow-gold hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Claim your free session
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Booking Section */}
      <section className="py-16 px-4 bg-brand-sage/10">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-serif font-semibold text-brand-navy mb-3 md:mb-6 text-center">{t('home:quickBooking.title')}</h2>
          <p className="text-brand-navy/80 mb-6 md:mb-8 text-center text-sm md:text-base">{t('home:quickBooking.description')}</p>
          
          <div className="bg-white rounded-xl shadow-lg p-5 md:p-8">
            <form onSubmit={handleQuickBooking} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-1 md:space-y-2">
                <label htmlFor="service" className="block text-sm font-medium text-brand-navy">Select Service</label>
                <select
                  id="service"
                  value={quickBooking.serviceId}
                  onChange={(e) => setQuickBooking({...quickBooking, serviceId: e.target.value})}
                  className="w-full rounded bg-cream-50 border border-brand-sage px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-brand-sage"
                  required
                >
                  <option value="">Choose a service...</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} - ${service.numericPrice || service.price}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1 md:space-y-2">
                <label htmlFor="date" className="block text-sm font-medium text-brand-navy">Preferred Date (Optional)</label>
                <input
                  type="date"
                  id="date"
                  value={quickBooking.date}
                  onChange={(e) => setQuickBooking({...quickBooking, date: e.target.value})}
                  className="w-full rounded bg-cream-50 border border-brand-sage px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-brand-sage"
                />
              </div>
              
              <div className="md:col-span-2 mt-2">
                <button type="submit" className="w-full bg-brand-sage hover:bg-brand-sage-deep text-white py-3 rounded-md font-semibold text-base md:text-lg transition-colors flex items-center justify-center">
                  Check Availability
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full py-20 px-4 bg-cream-100/40">
        <div className="container mx-auto max-w-6xl">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <Card variant="feature" className="flex flex-col items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-brand-sage/25 flex items-center justify-center text-brand-teal">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif text-brand-navy">Expert therapists</h3>
              <p className="text-brand-navy/70 text-sm leading-relaxed">Certified estheticians who bring craft and quiet care to every treatment.</p>
            </Card>
            <Card variant="feature" className="flex flex-col items-start gap-4 md:translate-y-6">
              <div className="w-11 h-11 rounded-full bg-brand-blush/30 flex items-center justify-center text-brand-navy">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
              </div>
              <h3 className="text-xl font-serif text-brand-navy">Customized treatments</h3>
              <p className="text-brand-navy/70 text-sm leading-relaxed">Personalized rituals tailored to your skin, your nervous system, your day.</p>
            </Card>
            <Card variant="feature" className="flex flex-col items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif text-brand-navy">Premium products</h3>
              <p className="text-brand-navy/70 text-sm leading-relaxed">Organic, scientifically-grounded ingredients sourced from labels we trust.</p>
            </Card>
            <Card variant="feature" className="flex flex-col items-start gap-4 md:translate-y-6">
              <div className="w-11 h-11 rounded-full bg-brand-sage/25 flex items-center justify-center text-brand-teal">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif text-brand-navy">A space made for stillness</h3>
              <p className="text-brand-navy/70 text-sm leading-relaxed">Sound, scent, and light tuned so your body can drop into rest.</p>
            </Card>
          </Stagger>
        </div>
      </section>

      {/* Featured Services (updated above) */}
      <section className="w-full py-20 px-4 bg-white/50">
        <div className="container mx-auto">
          <Reveal className="text-center mb-12">
            <span className="block text-xs tracking-[0.22em] text-brand-teal uppercase mb-3">The menu</span>
            <h2 className="text-display-lg font-serif text-brand-navy mb-4">Our spa rituals</h2>
            <p className="text-brand-navy/75 max-w-2xl mx-auto">A spectrum of treatments designed to relax, rejuvenate, and restore balance to your body and mind.</p>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Massage Therapy */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3865676/pexels-photo-3865676.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Relaxing massage therapy session at Healing Lotus Spa Dallas with aromatic oils"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Massage Therapy</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Relieve tension and promote deep relaxation with our therapeutic massage services.</p>
                <Link to="/services" className="text-brand-sage text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
            
            {/* Facial Treatments */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3997993/pexels-photo-3997993.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Professional facial treatment at a Dallas luxury spa with natural skincare products"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Facial Treatments</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Revitalize your skin with our customized facials for a healthy, radiant complexion.</p>
                <Link to="/services" className="text-brand-sage text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
            
            {/* Body Treatments */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/3188/love-romantic-bath-candlelight.jpg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Luxurious body treatment at Healing Lotus Dallas with candles and aromatic bath"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Body Treatments</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Indulge in our premium body treatments designed for total relaxation and rejuvenation.</p>
                <Link to="/services" className="text-brand-sage text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
            
            {/* Specialty Treatments */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Specialty hot stone spa treatment at Healing Lotus Spa Dallas with premium oils"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-transparent flex flex-col justify-end p-6">
                <h3 className="text-xl font-semibold text-white mb-2 font-serif">Specialty Treatments</h3>
                <p className="text-white/90 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">Experience our signature treatments that combine multiple techniques for the ultimate spa experience.</p>
                <Link to="/services" className="text-brand-sage text-sm font-medium group-hover:underline transition-all flex items-center">
                  Explore Services
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Price Guide */}
      <section className="py-20 px-4 bg-cream-50">
        <div className="container mx-auto">
          <Reveal className="text-center mb-12">
            <span className="block text-xs tracking-[0.22em] text-brand-teal uppercase mb-3">Starting from</span>
            <h2 className="text-display-lg font-serif text-brand-navy mb-4">Pricing guide</h2>
            <p className="text-brand-navy/75 max-w-2xl mx-auto">Our most popular treatments at a glance. Visit the services page for the full menu.</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Dynamic Pricing Cards - Show cheapest service from each category */}
            {categories.slice(0, 4).map((category) => {
              const cheapest = cheapestServices[category.id];
              if (!cheapest) return null;

              return (
                <Card key={category.id} variant="service">
                  <h3 className="text-xl font-serif text-brand-navy mb-4">
                    {category.name || category.title}
                  </h3>
                  <div className="mb-4">
                    <p className="text-sm text-brand-navy/70 mb-1">{cheapest.name}</p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-2xl font-serif text-brand-navy tabular-nums">
                        ${cheapest.numericPrice || cheapest.price}
                      </p>
                      <p className="text-sm text-brand-navy/60 tabular-nums">
                        {cheapest.numericDuration || cheapest.duration} min
                      </p>
                    </div>
                  </div>
                  <Link to="/services" className="text-brand-teal text-sm font-medium tracking-wide hover:text-brand-navy transition-colors flex items-center">
                    View details
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services" className="inline-flex items-center justify-center bg-brand-navy/5 hover:bg-brand-navy/10 text-brand-navy px-6 py-3 rounded-md font-medium tracking-wide transition-colors">
              View complete service menu
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Wallet Top-Up Bonus Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-brand-sage/20 via-white to-brand-blush/20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-brand-sage/10 blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-blush/10 blur-3xl"></div>

        <div className="container mx-auto max-w-5xl relative">
          <Reveal className="text-center mb-12">
            <span className="block text-xs tracking-[0.22em] text-brand-teal uppercase mb-3">A small thank you</span>
            <h2 className="text-display-lg font-serif text-brand-navy mb-4">Wallet top-up bonus</h2>
            <p className="text-brand-navy/75 max-w-2xl mx-auto">
              Add funds to your wallet and receive bonus credit. The more you top up, the bigger the bonus.
            </p>
          </Reveal>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { topUp: 500, bonus: 100, total: 600 },
              { topUp: 1000, bonus: 300, total: 1300 },
              { topUp: 2000, bonus: 1000, total: 3000 },
              { topUp: 3000, bonus: 1500, total: 4500 },
              { topUp: 4000, bonus: 2500, total: 6500 },
              { topUp: 5000, bonus: 3000, total: 8000 },
            ].map((tier) => (
              <Card key={tier.topUp} variant="service" className="text-center">
                <p className="text-3xl font-serif text-brand-navy mb-3 tabular-nums">${tier.topUp.toLocaleString()}</p>
                <span className="inline-block px-3 py-1 bg-brand-sage/30 text-brand-navy text-sm font-medium rounded-full mb-3 tabular-nums">
                  +${tier.bonus.toLocaleString()} bonus
                </span>
                <p className="text-brand-navy/70 text-sm">Total credit <span className="font-semibold text-brand-navy tabular-nums">${tier.total.toLocaleString()}</span></p>
              </Card>
            ))}
          </Stagger>

          <div className="text-center mt-12">
            <button
              onClick={handleTopUpClick}
              className="inline-flex items-center justify-center bg-brand-sage hover:bg-brand-sage-deep text-brand-navy hover:text-white px-8 py-4 rounded-md font-semibold tracking-wide transition duration-300 hover:shadow-sage-lg hover:-translate-y-0.5 active:scale-[0.98]"
            >
              Top up now
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 md:py-24 px-4">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-16">
            <Reveal className="w-full lg:w-1/2 rounded-2xl overflow-hidden shadow-cream">
              <img
                src="https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                srcSet="
                  https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1 600w,
                  https://images.pexels.com/photos/3865675/pexels-photo-3865675.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1 1260w
                "
                sizes="(max-width: 1023px) 100vw, 50vw"
                alt="Tranquil spa treatment room at Healing Lotus Dallas with hot stones and candles"
                className="w-full h-[440px] object-cover"
                loading="lazy"
              />
            </Reveal>
            <Reveal className="w-full lg:w-1/2">
              <span className="block text-xs tracking-[0.22em] text-brand-teal uppercase mb-3">About</span>
              <h2 className="text-display-lg font-serif text-brand-navy mb-6">Healing Lotus Skin Spa</h2>
              <h3 className="text-xl font-serif italic text-brand-navy/80 mb-4">Emotional skincare & healing aesthetics — awakening the skin through sound</h3>
              <p className="text-brand-navy/80 mb-4 leading-relaxed">
                True beauty begins when emotions are gently held.
              </p>
              <p className="text-brand-navy/80 mb-6 leading-relaxed">
                Emotional Skincare Healing Aesthetics integrates skin science, nervous system relaxation, and sound therapy. We believe skin responds not only to ingredients, but to emotions. When stress, anxiety, and inner tension are released, skin naturally returns to balance and radiance.
              </p>
              <p className="text-brand-navy/80 mb-6 leading-relaxed">
                Each treatment pairs professional skincare with therapeutic sound frequencies—low-frequency singing bowls, ambient tones, rhythmic breathing guidance, and healing sound waves. These sounds calm the nervous system and activate the body's natural repair mode.
              </p>
              <div className="bg-cream-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-brand-navy/90 mb-2 font-medium">As sound moves through the space:</p>
                <ul className="text-sm text-brand-navy/80 space-y-1">
                  <li>• The mind quiets</li>
                  <li>• Breathing deepens</li>
                  <li>• Tension softens</li>
                  <li>• Skin absorption enhances</li>
                </ul>
              </div>
              <p className="text-brand-navy/80 mb-6 leading-relaxed italic">
                Skincare transcends the surface, reaching emotional, neurological, and energetic layers—allowing the skin to heal while being truly "heard."
              </p>
              <p className="text-brand-navy font-medium mb-8">
                This is not simply a beauty treatment—<br />
                <span className="text-brand-navy font-semibold">it's a journey of emotional restoration and skin rebirth.</span>
              </p>
              <Link to="/about" className="inline-flex items-center justify-center bg-brand-blush hover:bg-[#e6b9b8] text-brand-navy px-7 py-3 rounded-md font-medium tracking-wide transition duration-300 hover:-translate-y-0.5 active:scale-[0.98]">
                Learn more about us
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Updated */}
      <section className="py-20 md:py-24 px-4 bg-cream-100/50">
        <div className="container mx-auto max-w-6xl">
          <Reveal className="text-center mb-14">
            <span className="block text-xs tracking-[0.22em] text-brand-teal uppercase mb-3">In their words</span>
            <h2 className="text-display-lg font-serif text-brand-navy mb-4">Client testimonials</h2>
            <p className="text-brand-navy/75 max-w-2xl mx-auto">A few notes from people who walked through our doors and left a little lighter.</p>
          </Reveal>

          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              { initials: 'ST', name: 'Sarah T.', tag: 'Dallas — regular client',
                quote: '"The atmosphere at Healing Lotus is truly transformative. From the moment I stepped in, I felt the stress melt away. The massage was incredible, and the staff made me feel so special."' },
              { initials: 'AR', name: 'Amanda R.', tag: 'Dallas — monthly member',
                quote: '"Great experience from start to finish. The aromatherapy and attention to detail made my facial treatment exceptional. The staff at Healing Lotus are true professionals who know exactly what your skin needs."' },
              { initials: 'MD', name: 'Michael D.', tag: 'Dallas — first-time guest',
                quote: '"My first visit to Healing Lotus Spa exceeded all my expectations — the place is gorgeous, clean and peaceful. The treatments were incredible and I can\'t wait to return!"' },
            ].map((t) => (
              <figure key={t.name} className="flex flex-col gap-6">
                <div className="flex gap-1 text-brand-gold text-base" aria-label="5 out of 5 stars rating">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} role="img" aria-hidden="true">★</span>
                  ))}
                </div>
                <blockquote className="text-lg md:text-xl font-serif text-brand-navy leading-relaxed">
                  {t.quote}
                </blockquote>
                <figcaption className="flex items-center gap-3 pt-4 border-t border-brand-sage/30">
                  <div className="w-10 h-10 rounded-full bg-brand-sage/30 ring-1 ring-brand-sage flex items-center justify-center text-brand-navy text-sm font-semibold tracking-wider">
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-brand-navy">{t.name}</p>
                    <p className="text-brand-navy/60 text-sm">{t.tag}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Premium CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-brand-blush/30 via-white to-brand-sage/30 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-brand-blush/10 blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-sage/10 blur-3xl"></div>
        
        <div className="container mx-auto max-w-5xl relative">
          <Reveal className="bg-cream-50/95 rounded-2xl shadow-cream p-8 md:p-12 ring-1 ring-brand-sage/20">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* Left: Text Content */}
              <div className="w-full md:w-2/3">
                <span className="inline-block px-4 py-1 bg-brand-sage/30 text-brand-navy text-xs font-semibold tracking-[0.2em] uppercase rounded-full mb-5">Begin your journey</span>
                <h2 className="text-display-lg font-serif text-brand-navy mb-6">Transform your well-being today</h2>
                <p className="text-brand-navy/75 text-lg mb-8 leading-relaxed">
                  Old craft meets modern care in our quiet sanctuary. Our therapists are ready to guide you, in your time.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/services" className="inline-flex items-center justify-center bg-brand-sage hover:bg-brand-sage-deep text-brand-navy hover:text-white px-8 py-4 rounded-md font-semibold tracking-wide transition duration-300 hover:shadow-sage-lg hover:-translate-y-0.5 active:scale-[0.98]">
                    Book your session
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                  <Link to="/membership" className="inline-flex items-center justify-center bg-transparent border border-brand-blush text-brand-navy hover:bg-brand-blush/15 px-8 py-3.5 rounded-md font-medium tracking-wide transition duration-300 hover:-translate-y-0.5">
                    Join membership
                  </Link>
                </div>
              </div>

            </div>
          </Reveal>
        </div>
      </section>

      {/* Floating Promo Popup - Bottom Left */}
      {showPromoPopup && (
        <div className="fixed bottom-6 left-6 z-50 animate-[slideUp_0.5s_ease-out]">
          <div
            onClick={handlePromoClick}
            className="relative bg-white rounded-xl shadow-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.2)] hover:-translate-y-1 border border-brand-gold/30 w-[320px]"
          >
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPromoPopup(false);
              }}
              className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white text-xs transition-colors"
              aria-label="Close promotion"
            >
              ✕
            </button>

            {/* Image — LA MER The Revitalizing Hydration Facial */}
            <div className="relative h-36 bg-cream-50">
              <picture>
                <source type="image/webp" srcSet="/images/lamer.webp" />
                <img
                  src="/images/lamer.jpeg"
                  alt="LA MER The Revitalizing Hydration Facial"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-cream-50/30 via-transparent to-transparent"></div>
              <div className="absolute bottom-3 left-3">
                <span className="inline-block px-3 py-1 bg-[#B83A3A] text-white text-xs font-bold tracking-wider rounded-full shadow-lg animate-pulse">
                  BUY 1 GET 1 FREE
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="font-serif font-semibold text-brand-navy text-base leading-snug mb-0.5">
                Buy 1 get 1 <span className="italic text-brand-gold">free.</span>
              </p>
              <p className="text-brand-navy/60 text-xs mb-3">
                LA MER Hydration Facial · 80 min · Open Spa Activity
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-xl font-semibold text-brand-navy tabular-nums">
                    $239.90
                  </span>
                  <span className="text-xs text-[#C41E3A] font-semibold tabular-nums">
                    2 sessions
                  </span>
                </div>
                <span className="inline-flex items-center bg-brand-gold text-white px-4 py-1.5 rounded-lg text-sm font-semibold">
                  Reserve
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Home; 